import React, { useEffect, useState, useCallback } from "react";
import { pickPortfolio } from "./selectedPortfolio";
import { API } from "./shared";
import { useNavigate } from "react-router-dom";
import {
  Loader2, Plus, School, Mail, Search, Archive, ArchiveRestore,
  Pencil, Send, ExternalLink, Building2, Clock,
} from "lucide-react";
import { toast } from "sonner";
import { SchoolGridRow, SchoolsGridHeader } from "./SchoolGridRow";
import { periodsForSchool } from "./portfolioFlow";

const DIAG_BADGE = {
  not_started: { label: "Not Started", cls: "bg-slate-100 text-slate-600 border-slate-200" },
  in_progress: { label: "In Progress", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  submitted: { label: "Submitted", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  approved: { label: "Completed", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  locked: { label: "Completed", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

const emptyAdd = {
  mode: "directory", school_name: "", school_directory_id: null, city: "", state: "",
  contact_first_name: "", contact_last_name: "", contact_email: "", contact_title: "", contact_phone: "",
};

const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC"];

export default function SchoolsManagement() {
  const navigate = useNavigate();
  const [pid, setPid] = useState(null);
  const [pname, setPname] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [busy, setBusy] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(emptyAdd);
  const [dirQuery, setDirQuery] = useState("");
  const [dirState, setDirState] = useState("");
  const [dirResults, setDirResults] = useState([]);
  const [dirSearching, setDirSearching] = useState(false);
  const [editing, setEditing] = useState(null); // row being edited
  const [periods, setPeriods] = useState([]);
  const [periodFilter, setPeriodFilter] = useState("");
  const [editForm, setEditForm] = useState({ school_name: "", contact_first_name: "", contact_last_name: "", contact_email: "" });

  const load = useCallback(async (portfolioId, archived) => {
    const r = await fetch(`${API}/api/portfolio/${portfolioId}/manage/schools?include_archived=${archived ? "true" : "false"}`, { credentials: "include" });
    if (r.ok) setData(await r.json());
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/api/portfolio/me`, { credentials: "include" });
        const me = await r.json();
        const p = pickPortfolio(me.portfolios);
        if (!p) { setLoading(false); return; }
        setPid(p.id); setPname(p.name);
        await load(p.id, showArchived);
        const pr = await fetch(`${API}/api/portfolio/${p.id}/periods`, { credentials: "include" });
        if (pr.ok) setPeriods((await pr.json()).periods || []);
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load]);

  useEffect(() => { if (pid) load(pid, showArchived); }, [showArchived, pid, load]);

  // Directory search (debounced), focused by the selected state
  useEffect(() => {
    if (form.mode !== "directory" || dirQuery.trim().length < 2) { setDirResults([]); return; }
    const t = setTimeout(async () => {
      setDirSearching(true);
      try {
        const qs = `q=${encodeURIComponent(dirQuery.trim())}${dirState ? `&state=${dirState}` : ""}`;
        const r = await fetch(`${API}/api/portfolio/${pid}/school-directory/search?${qs}`, { credentials: "include" });
        if (r.ok) { const d = await r.json(); setDirResults(d.results || []); }
      } finally { setDirSearching(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [dirQuery, dirState, form.mode, pid]);

  const pickDirectory = (s) => {
    setForm((f) => ({ ...f, school_name: s.school_name, school_directory_id: s.school_directory_id, city: s.city, state: s.state }));
    setDirResults([]); setDirQuery(`${s.school_name}${s.city ? ` — ${s.city}, ${s.state}` : ""}`);
  };

  const openAdd = () => {
    const st = data?.default_state || "";
    setForm({ ...emptyAdd, state: st }); setDirQuery(""); setDirResults([]); setDirState(st); setShowAdd(true);
  };

  const submitAdd = async () => {
    if (!form.school_name.trim()) { toast.error("Enter a school name"); return; }
    if (!form.contact_email.trim() || !form.contact_first_name.trim()) { toast.error("Enter the primary contact's name and email"); return; }
    setBusy("add");
    try {
      const r = await fetch(`${API}/api/portfolio/${pid}/manage/schools`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          school_name: form.school_name.trim(), school_directory_id: form.school_directory_id,
          city: form.city, state: form.state,
          contact_first_name: form.contact_first_name.trim(), contact_last_name: form.contact_last_name.trim(),
          contact_email: form.contact_email.trim(), contact_title: form.contact_title.trim(), contact_phone: form.contact_phone.trim(),
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Could not add school");
      toast.success(d.email_sent ? "School added — invitation sent" : "School added — invitation created");
      setShowAdd(false); load(pid, showArchived);
    } catch (e) { toast.error(e.message); } finally { setBusy(""); }
  };

  const resend = async (row) => {
    setBusy(row.id);
    try {
      const r = await fetch(`${API}/api/portfolio/${pid}/manage/schools/${row.id}/resend`, { method: "POST", credentials: "include" });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Could not resend");
      toast.success(d.email_sent ? "Invitation resent" : "Invitation regenerated");
      if (d.invite_link) { try { await navigator.clipboard.writeText(d.invite_link); } catch {} }
      load(pid, showArchived);
    } catch (e) { toast.error(e.message); } finally { setBusy(""); }
  };

  const remindPeriod = async (row) => {
    setBusy(row.id);
    try {
      const r = await fetch(`${API}/api/portfolio/${pid}/manage/schools/${row.id}/remind-period-name`, { method: "POST", credentials: "include" });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Could not send the reminder");
      toast.success(d.sent === d.recipients
        ? `Reminder sent to ${d.sent} school admin${d.sent === 1 ? "" : "s"}`
        : `Reminder delivered to ${d.sent} of ${d.recipients} school admins`);
      load(pid, showArchived);
    } catch (e) { toast.error(e.message); } finally { setBusy(""); }
  };

  const archive = async (row) => {
    if (!window.confirm(`Archive ${row.school_name}? Its diagnostic history is preserved and it can be restored anytime.`)) return;
    setBusy(row.id);
    try {
      const r = await fetch(`${API}/api/portfolio/${pid}/manage/schools/${row.id}/archive`, { method: "POST", credentials: "include" });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Could not archive");
      toast.success("School archived"); load(pid, showArchived);
    } catch (e) { toast.error(e.message); } finally { setBusy(""); }
  };

  const unarchive = async (row) => {
    setBusy(row.id);
    try {
      const r = await fetch(`${API}/api/portfolio/${pid}/manage/schools/${row.id}/unarchive`, { method: "POST", credentials: "include" });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Could not restore");
      toast.success("School restored"); load(pid, showArchived);
    } catch (e) { toast.error(e.message); } finally { setBusy(""); }
  };

  const openEdit = (row) => {
    setEditing(row);
    setEditForm({
      school_name: row.school_name || "",
      contact_first_name: (row.contact?.name || "").split(" ")[0] || "",
      contact_last_name: (row.contact?.name || "").split(" ").slice(1).join(" ") || "",
      contact_email: row.contact?.email || "",
    });
  };

  const submitEdit = async () => {
    setBusy("edit");
    try {
      const payload = editing.kind === "pending"
        ? editForm
        : { school_name: editForm.school_name };
      const r = await fetch(`${API}/api/portfolio/${pid}/manage/schools/${editing.id}`, {
        method: "PATCH", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Could not save");
      toast.success("Saved"); setEditing(null); load(pid, showArchived);
    } catch (e) { toast.error(e.message); } finally { setBusy(""); }
  };

  if (loading) return <div className="py-16 text-center text-slate-500"><Loader2 className="h-5 w-5 animate-spin inline" /></div>;
  if (!pid) return <div className="py-16 text-center text-slate-500" data-testid="schools-no-portfolio">No portfolio found for your account.</div>;

  const canManage = data?.can_manage;
  const canArchive = data?.can_archive;
  const schools = data?.schools || [];
  const counts = data?.counts || {};

  return (
    <div data-testid="schools-management-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
        <h1 className="text-xl font-bold text-slate-900">Schools</h1>
        {canManage && (
          <button onClick={openAdd} data-testid="school-add-open"
            className="inline-flex items-center gap-2 rounded-md bg-[#007a9e] hover:bg-[#00607d] text-white text-sm font-medium px-4 py-2">
            <Plus className="h-4 w-4" /> Add School
          </button>
        )}
      </div>
      <p className="text-sm text-slate-500 mb-4">Manage the schools in <strong>{pname}</strong> — add new schools anytime, edit details, or archive.</p>

      <div className="flex flex-wrap items-center gap-4 mb-5 text-sm">
        <span className="text-slate-600" data-testid="schools-count-active"><strong>{counts.active || 0}</strong> active</span>
        <span className="text-slate-600" data-testid="schools-count-pending"><strong>{counts.pending || 0}</strong> pending activation</span>
        {periods.length > 0 && (
          <label className="inline-flex items-center gap-2 text-slate-600">
            Period
            <select value={periodFilter} onChange={(e) => setPeriodFilter(e.target.value)} data-testid="schools-period-filter"
              className="rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm">
              <option value="">Latest for each school</option>
              {periods.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </label>
        )}
        <label className="ml-auto inline-flex items-center gap-2 text-slate-600 cursor-pointer" data-testid="schools-archived-toggle">
          <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} className="rounded border-slate-300" />
          Show archived ({counts.archived || 0})
        </label>
      </div>

      <SchoolsGridHeader />
      <div className="space-y-3">
        {schools.map((row) => {
          const flows = row.kind === "active" ? periodsForSchool(periods, row.tenant_id) : [];
          const entry = periodFilter ? flows.find((x) => x.period.id === periodFilter) : flows[0];
          return (
            <SchoolGridRow key={row.id} row={row} entry={entry} hasAnyPeriod={flows.length > 0 || periods.some((p) => p.status === "draft" && (p.school_ids || []).includes(row.tenant_id))} pid={pid} canManage={canManage} canArchive={canArchive}
              busy={busy === row.id} navigate={navigate} onEdit={openEdit} onArchive={archive} onResend={resend} onUnarchive={unarchive} onRemindPeriod={remindPeriod} />
          );
        })}
        {schools.length === 0 && (
          <div className="rounded-xl border border-dashed border-slate-300 p-10 text-center text-slate-500" data-testid="schools-empty">
            <Building2 className="h-8 w-8 mx-auto text-slate-300" />
            <p className="mt-3 text-sm">No schools yet. Add your first school to invite it to complete the Diagnostic.</p>
          </div>
        )}
      </div>

      {/* Add School modal */}
      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setShowAdd(false)}>
          <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="school-add-modal">
            <h2 className="text-lg font-bold text-slate-900">Add a School</h2>
            <p className="text-[13px] text-slate-500 mt-1">The primary contact receives an invitation to set a password and activate their MySchoolRx workspace.</p>

            <div className="mt-4 inline-flex rounded-lg border border-slate-200 p-0.5 text-sm">
              <button onClick={() => setForm({ ...form, mode: "directory" })} data-testid="school-add-mode-directory"
                className={`px-3 py-1.5 rounded-md font-medium ${form.mode === "directory" ? "bg-[#007a9e] text-white" : "text-slate-600"}`}>From directory</button>
              <button onClick={() => setForm({ ...form, mode: "new", school_directory_id: null })} data-testid="school-add-mode-new"
                className={`px-3 py-1.5 rounded-md font-medium ${form.mode === "new" ? "bg-[#007a9e] text-white" : "text-slate-600"}`}>New school</button>
            </div>

            <div className="mt-4 space-y-3">
              {form.mode === "directory" ? (
                <div className="relative">
                  <div className="flex items-end gap-2">
                    <div className="w-28 shrink-0">
                      <label className="text-xs font-medium text-slate-500">State</label>
                      <select value={dirState} onChange={(e) => { setDirState(e.target.value); setForm((f) => ({ ...f, school_directory_id: null })); }}
                        data-testid="school-directory-state"
                        className="mt-1 w-full rounded-md border border-slate-200 px-2 py-2 text-sm bg-white">
                        <option value="">All states</option>
                        {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                    <div className="flex-1">
                      <label className="text-xs font-medium text-slate-500">Search the school directory *</label>
                      <div className="relative mt-1">
                        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
                        <input value={dirQuery} onChange={(e) => { setDirQuery(e.target.value); setForm((f) => ({ ...f, school_directory_id: null })); }}
                          placeholder="School name, city, or NCES ID" data-testid="school-directory-search"
                          className="w-full rounded-md border border-slate-200 pl-8 pr-3 py-2 text-sm" />
                      </div>
                    </div>
                  </div>
                  {(dirSearching || dirResults.length > 0) && (
                    <div className="absolute z-10 mt-1 w-full rounded-md border border-slate-200 bg-white shadow-lg max-h-56 overflow-y-auto" data-testid="school-directory-results">
                      {dirSearching && <div className="px-3 py-2 text-sm text-slate-400"><Loader2 className="h-4 w-4 animate-spin inline" /> Searching…</div>}
                      {!dirSearching && dirResults.map((s) => (
                        <button key={s.school_directory_id} onClick={() => pickDirectory(s)} data-testid={`school-directory-pick-${s.school_directory_id}`}
                          className="block w-full text-left px-3 py-2 text-sm hover:bg-slate-50">
                          <div className="font-medium text-slate-800">{s.school_name}</div>
                          <div className="text-xs text-slate-500">{[s.city, s.state].filter(Boolean).join(", ")}{s.nces_school_id ? ` · NCES ${s.nces_school_id}` : ""}</div>
                        </button>
                      ))}
                    </div>
                  )}
                  {form.school_directory_id && <p className="mt-1 text-xs text-emerald-600">Selected: {form.school_name}</p>}
                </div>
              ) : (
                <div><label className="text-xs font-medium text-slate-500">School name *</label>
                  <input value={form.school_name} onChange={(e) => setForm({ ...form, school_name: e.target.value })}
                    data-testid="school-add-name" className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm" /></div>
              )}

              <div className="pt-1 border-t border-slate-100">
                <p className="text-xs font-semibold text-slate-600 mt-3 mb-1">Primary contact</p>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="text-xs font-medium text-slate-500">First name *</label>
                    <input value={form.contact_first_name} onChange={(e) => setForm({ ...form, contact_first_name: e.target.value })}
                      data-testid="school-add-contact-first" className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm" /></div>
                  <div><label className="text-xs font-medium text-slate-500">Last name</label>
                    <input value={form.contact_last_name} onChange={(e) => setForm({ ...form, contact_last_name: e.target.value })}
                      data-testid="school-add-contact-last" className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm" /></div>
                </div>
                <div className="mt-3"><label className="text-xs font-medium text-slate-500">Email *</label>
                  <input type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                    data-testid="school-add-contact-email" className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm" /></div>
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <div><label className="text-xs font-medium text-slate-500">Title</label>
                    <input value={form.contact_title} onChange={(e) => setForm({ ...form, contact_title: e.target.value })}
                      data-testid="school-add-contact-title" className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm" /></div>
                  <div><label className="text-xs font-medium text-slate-500">Phone</label>
                    <input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
                      data-testid="school-add-contact-phone" className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm" /></div>
                </div>
              </div>
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setShowAdd(false)} className="px-4 py-2 text-sm rounded-md border border-slate-200">Cancel</button>
              <button onClick={submitAdd} disabled={busy === "add"} data-testid="school-add-submit"
                className="px-4 py-2 text-sm rounded-md bg-[#007a9e] hover:bg-[#00607d] text-white inline-flex items-center gap-2">
                {busy === "add" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Add & Send Invite
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit modal */}
      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setEditing(null)}>
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="school-edit-modal">
            <h2 className="text-lg font-bold text-slate-900">Edit School</h2>
            <div className="mt-4 space-y-3">
              <div><label className="text-xs font-medium text-slate-500">School name</label>
                <input value={editForm.school_name} onChange={(e) => setEditForm({ ...editForm, school_name: e.target.value })}
                  data-testid="school-edit-name" className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm" /></div>
              {editing.kind === "pending" && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="text-xs font-medium text-slate-500">Contact first name</label>
                      <input value={editForm.contact_first_name} onChange={(e) => setEditForm({ ...editForm, contact_first_name: e.target.value })}
                        data-testid="school-edit-contact-first" className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm" /></div>
                    <div><label className="text-xs font-medium text-slate-500">Contact last name</label>
                      <input value={editForm.contact_last_name} onChange={(e) => setEditForm({ ...editForm, contact_last_name: e.target.value })}
                        data-testid="school-edit-contact-last" className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm" /></div>
                  </div>
                  <div><label className="text-xs font-medium text-slate-500">Contact email</label>
                    <input type="email" value={editForm.contact_email} onChange={(e) => setEditForm({ ...editForm, contact_email: e.target.value })}
                      data-testid="school-edit-contact-email" className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm" /></div>
                </>
              )}
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="px-4 py-2 text-sm rounded-md border border-slate-200">Cancel</button>
              <button onClick={submitEdit} disabled={busy === "edit"} data-testid="school-edit-submit"
                className="px-4 py-2 text-sm rounded-md bg-[#007a9e] hover:bg-[#00607d] text-white inline-flex items-center gap-2">
                {busy === "edit" ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
